import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Security as SecurityIcon,
  Check as CheckIcon,
  VpnKey as KeyIcon
} from '@mui/icons-material';
import { AuthService, PasswordChangeResult } from '../services/auth';

interface AdminPasswordChangeProps {
  passwordChangeData: PasswordChangeResult;
  onPasswordChanged: (result: any) => void;
  onError: (error: string) => void;
}

export const AdminPasswordChange: React.FC<AdminPasswordChangeProps> = ({
  passwordChangeData,
  onPasswordChanged,
  onError
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('At least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('At least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('At least one lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('At least one number');
    }
    // Note: Special characters are not required by this Cognito User Pool
    
    return errors;
  };

  const handlePasswordChange = (password: string) => {
    setNewPassword(password);
    setValidationErrors(validatePassword(password));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      onError('Passwords do not match');
      return;
    }

    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      onError('Password does not meet requirements');
      return;
    }

    setLoading(true);

    try {
      const result = await AuthService.completeNewPasswordChallenge(
        passwordChangeData.cognitoUser,
        newPassword,
        passwordChangeData.userAttributes
      );
      
      onPasswordChanged(result);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const isPasswordValid = validationErrors.length === 0 && newPassword.length > 0;
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  return (
    <Card elevation={8} sx={{ borderRadius: 3, maxWidth: 500, mx: 'auto' }}>
      <CardContent sx={{ p: 4 }}>
        {/* Header */}
        <Box textAlign="center" mb={3}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'var(--gradient-background, linear-gradient(135deg, #667eea 0%, #764ba2 100%))',
              mb: 2
            }}
          >
            <KeyIcon sx={{ fontSize: 30, color: 'white' }} />
          </Box>
          <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
            Change Password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your temporary password must be changed before you can continue
          </Typography>
        </Box>

        {/* Security Notice */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            mb: 3,
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            borderRadius: 2,
            border: '1px solid rgba(25, 118, 210, 0.2)'
          }}
        >
          <SecurityIcon color="primary" />
          <Typography variant="body2" color="primary">
            Choose a strong password to secure your admin account
          </Typography>
        </Box>

        {/* Password Requirements */}
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Password Requirements:
          </Typography>
          <List dense>
            {[
              'At least 8 characters long',
              'At least one uppercase letter',
              'At least one lowercase letter', 
              'At least one number'
            ].map((requirement, index) => {
              const isMet = newPassword.length > 0 && !validatePassword(newPassword).includes(requirement);
              return (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckIcon 
                      sx={{ 
                        fontSize: 16, 
                        color: isMet ? 'success.main' : 'text.disabled' 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={requirement}
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        fontSize: '0.875rem',
                        color: isMet ? 'success.main' : 'text.secondary'
                      } 
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* Password Change Form */}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => handlePasswordChange(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            error={newPassword.length > 0 && !isPasswordValid}
            helperText={
              newPassword.length > 0 && !isPasswordValid 
                ? `Missing: ${validationErrors.join(', ')}`
                : ''
            }
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            error={confirmPassword.length > 0 && !passwordsMatch}
            helperText={
              confirmPassword.length > 0 && !passwordsMatch 
                ? 'Passwords do not match'
                : ''
            }
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !isPasswordValid || !passwordsMatch}
            startIcon={loading ? <CircularProgress size={20} /> : <KeyIcon />}
            sx={{
              py: 1.5,
              background: 'var(--gradient-background, linear-gradient(135deg, #667eea 0%, #764ba2 100%))',
              '&:hover': {
                opacity: 0.9
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
              }
            }}
          >
            {loading ? 'Changing Password...' : 'Change Password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};